import React from 'react';
import { InputBaseComponentProps, TextField as MUITextField } from '@mui/material';
import identity from 'lodash/identity';
import { Controller, FieldError, useFormContext } from 'react-hook-form';

import { Label } from '@src/components/Typography';
import { palette } from '@src/theme';
import { getTestId } from '@src/utils/testIds';
import { TextFieldProps } from './interface';
import { BASE_TEXT_FIELD_HEIGHT } from './util';
import { VStack } from '@src/components/VStack';

export const TextField = (props: TextFieldProps) => {
  const {
    disabled,
    formatOnChange = identity,
    helperText,
    inputRef,
    label,
    multiline,
    name,
    parseValue = identity,
    placeholder,
    rows,
    type,
  } = props;
  const { control } = useFormContext();

  const getHelperText = (error: FieldError | undefined) => {
    const parsedError = error || undefined;
    return parsedError ? parsedError.message : helperText;
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value: fieldValue }, fieldState: { error } }) => (
        <VStack>
          <Label size="s">{label}</Label>
          <MUITextField
            disabled={disabled}
            error={!!error}
            fullWidth
            helperText={getHelperText(error)}
            inputProps={createInputProps(props)}
            multiline={multiline}
            name={name}
            onChange={(e) => {
              const val = e.target.value;
              const formattedValue = formatOnChange(val, fieldValue);
              onChange(formattedValue);
            }}
            placeholder={placeholder ? placeholder : label}
            inputRef={inputRef}
            rows={rows}
            type={type}
            value={parseValue(fieldValue)}
          />
        </VStack>
      )}
    />
  );
};

const createInputProps = (props: TextFieldProps): InputBaseComponentProps => {
  const { ariaLabel, maxLength, onClick, readOnly, type } = props;
  const inputProps: InputBaseComponentProps = {
    'aria-label': ariaLabel,
    'data-testid': getTestId(props),
    maxLength,
    onClick,
    readOnly,
    sx: {
      boxSizing: 'border-box',
      fontSize: '14px',
      height: `${BASE_TEXT_FIELD_HEIGHT}px`,
      '::placeholder': {
        color: palette.controls.outline,
        opacity: '1',
      },
    },
    type,
  };

  return inputProps;
};
